import React, { useState, useEffect, useRef, useCallback } from 'react';
import { List } from 'immutable';
import { useAppDispatch } from 'flow/hooks/useRedux';
import { MessageContent } from 'flow/components/tickets/UseAttachmentHandler';
import { TicketKey } from 'flow/types/EntityKeys';
import { PickedMention } from 'flow/types/Tickets';
import { TypedMap } from 'flow/types/TypedMap';
import { shiftPickedMentions } from 'flow/utils/ticket-utils';
import { setTicketReplyDraft } from '../InputActions';

interface UseTicketMessageContentParams {
    ticketKey: TicketKey;
    internalDraft: string;
    internalDraftMentions?: List<TypedMap<PickedMention>>;
    replyExternalDraft: string;
    inputFieldRef: React.RefObject<HTMLTextAreaElement>;
}

interface UseTicketMessageContentReturn {
    messageContentInternal: MessageContent;
    setMessageContentInternal: React.Dispatch<
        React.SetStateAction<MessageContent>
    >;
    messageContentReplyExternal: MessageContent;
    setMessageContentReplyExternal: React.Dispatch<
        React.SetStateAction<MessageContent>
    >;
    updateMessageAndCaretPosition: (
        text: string,
        caretPosition: number,
        tabSelected: 'internal' | 'reply_external',
    ) => void;
}

export const useTicketMessageContent = ({
    ticketKey,
    internalDraft,
    internalDraftMentions,
    replyExternalDraft,
    inputFieldRef,
}: UseTicketMessageContentParams): UseTicketMessageContentReturn => {
    const dispatch = useAppDispatch();

    const [messageContentInternal, setMessageContentInternal] =
        useState<MessageContent>({
            text: internalDraft || '',
            attachment: null,
            imageSrc: null,
            mentions: internalDraftMentions,
        });

    const [messageContentReplyExternal, setMessageContentReplyExternal] =
        useState<MessageContent>({
            text: replyExternalDraft || '',
            attachment: null,
            imageSrc: null,
        });

    // The input component isn't remounted when switching tickets, so reset the
    // editable content to the new ticket's drafts when the ticket changes —
    // otherwise unsent text/attachments carry over (the draft-value sync effects
    // below don't fire when two tickets share the same, e.g. empty, draft). This
    // is React's "reset state when a prop changes" pattern (set during render).
    const [contentTicketKey, setContentTicketKey] = useState(ticketKey);
    if (ticketKey !== contentTicketKey) {
        setContentTicketKey(ticketKey);
        setMessageContentInternal({
            text: internalDraft || '',
            attachment: null,
            imageSrc: null,
            mentions: internalDraftMentions,
        });
        setMessageContentReplyExternal({
            text: replyExternalDraft || '',
            attachment: null,
            imageSrc: null,
        });
    }

    // Refs to access content on unmount for draft saving
    const contentInternalRefToAccessOnCleanup = useRef(messageContentInternal);
    const contentExternalReplyRefToAccessOnCleanup = useRef(
        messageContentReplyExternal,
    );

    // Update message content and caret position based on selected tab
    const updateMessageAndCaretPosition = useCallback(
        (
            text: string,
            caretPosition: number,
            tabSelected: 'internal' | 'reply_external',
        ) => {
            const setMessageContent =
                tabSelected === 'internal'
                    ? setMessageContentInternal
                    : setMessageContentReplyExternal;

            setMessageContent((prev) => ({
                ...prev,
                text,
                mentions: shiftPickedMentions(
                    prev.mentions,
                    prev.text,
                    text,
                    caretPosition,
                ),
            }));

            if (inputFieldRef.current) {
                inputFieldRef.current.value = text;
                inputFieldRef.current.setSelectionRange(
                    caretPosition,
                    caretPosition,
                );
                inputFieldRef.current.focus();
            }
        },
        [inputFieldRef],
    );

    // Sync internal draft changes from Redux
    useEffect(() => {
        setMessageContentInternal((prev) => ({
            ...prev,
            text: internalDraft,
        }));
    }, [internalDraft]);

    // Sync external draft changes from Redux
    useEffect(() => {
        setMessageContentReplyExternal((prev) => ({
            ...prev,
            text: replyExternalDraft,
        }));
    }, [replyExternalDraft]);

    // Update refs when content changes
    useEffect(() => {
        contentInternalRefToAccessOnCleanup.current = messageContentInternal;
    }, [messageContentInternal]);

    useEffect(() => {
        contentExternalReplyRefToAccessOnCleanup.current =
            messageContentReplyExternal;
    }, [messageContentReplyExternal]);

    // Save drafts on unmount
    useEffect(() => {
        return () => {
            dispatch(
                setTicketReplyDraft(
                    ticketKey,
                    'internal',
                    contentInternalRefToAccessOnCleanup.current?.text || '',
                    contentInternalRefToAccessOnCleanup.current?.mentions,
                ),
            );
            dispatch(
                setTicketReplyDraft(
                    ticketKey,
                    'external',
                    contentExternalReplyRefToAccessOnCleanup.current?.text ||
                        '',
                ),
            );
        };
    }, [dispatch, ticketKey]);

    return {
        messageContentInternal,
        setMessageContentInternal,
        messageContentReplyExternal,
        setMessageContentReplyExternal,
        updateMessageAndCaretPosition,
    };
};
